import Axios from '../config/axios-config';

const BASE = 'reports/dashboard';

const buildQs = (params) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

/** IANA del navegador (p. ej. America/Santiago) para alinear el dashboard con el día local. */
export function getBrowserIanaTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

export const dashboardService = {
  /**
   * Resumen operativo completo (KPIs del tab Operativo, cacheado en Redis).
   * @param {string} [timeZone] - IANA opcional; si se omite, el backend usa REPORTS_DEFAULT_TIMEZONE.
   */
  getOperationalSummary: (logisticCompanyId, date, timeZone) =>
    Axios.get(`${BASE}/operational-summary?${buildQs({ logisticCompanyId, date, timezone: timeZone })}`),

  getClientVolume: (logisticCompanyId, startDate, endDate, timeZone) =>
    Axios.get(`${BASE}/client-volume?${buildQs({ logisticCompanyId, startDate, endDate, timezone: timeZone })}`),

  getClientVolumeWeekly: (logisticCompanyId, startDate, endDate, timeZone) =>
    Axios.get(`${BASE}/client-volume-weekly?${buildQs({ logisticCompanyId, startDate, endDate, timezone: timeZone })}`),

  getDriverPerformance: (logisticCompanyId, startDate, endDate, timeZone) =>
    Axios.get(`${BASE}/driver-performance?${buildQs({ logisticCompanyId, startDate, endDate, timezone: timeZone })}`),

  getPendingBreakdown: (logisticCompanyId, startDate, endDate, timeZone) =>
    Axios.get(`${BASE}/pending-breakdown?${buildQs({ logisticCompanyId, startDate, endDate, timezone: timeZone })}`),

  /**
   * Resumen de pago contra entrega (COD) del operador.
   *
   * DELIBERADAMENTE NO RECIBE `logisticCompanyId`: el backend lo deriva del JWT.
   * El resto de los métodos de este archivo lo mandan por query string y el
   * backend confía en el parámetro (patrón param-trusted ya documentado);
   * replicarlo acá significaría que cualquiera con sesión puede leer los MONTOS
   * de otro operador cambiando un ObjectId en la URL. El precedente correcto es
   * driver-statistics, que sí lo deriva del token.
   *
   * Devuelve { currency, inRoute:{count,amount}, collected:{count,amount},
   * settled:{count,amount}, byMethod:[{method,count,amount}] }, donde:
   *   - `inRoute` es STOCK VIVO (pedidos en ruta con cobro pendiente AHORA), no
   *     depende del rango de fechas.
   *   - `collected` es el FLUJO del rango (anclado a cashOnDelivery.collectedAt).
   *   - `settled` es el subconjunto de `collected` que el operador realmente
   *     recauda (efectivo + transferencia al repartidor).
   *
   * @param {string} [timeZone] - IANA opcional; si se omite, el backend usa REPORTS_DEFAULT_TIMEZONE.
   */
  getCodSummary: (startDate, endDate, timeZone) =>
    Axios.get(`${BASE}/cod-summary?${buildQs({ startDate, endDate, timezone: timeZone })}`),

  /**
   * Paquetes "en riesgo" del día: no entregados y cerca del cutoff 21:00 en la
   * TZ del operador. Devuelve { count, critical, items:[{ followingNumber, lat,
   * lng, municipality, hoursToCutoff }] } y sirve tanto para el KPI como para
   * resaltar puntos en el mapa. (Endpoint nuevo de microservice-reports.)
   * @param {string} [timeZone] - IANA opcional (operationsCountry del operador).
   */
  getAtRisk: (logisticCompanyId, date, timeZone) =>
    Axios.get(`${BASE}/at-risk?${buildQs({ logisticCompanyId, date, timezone: timeZone })}`),
};
